import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';

import {
  HeaderRow,
  PageWrapper,
  PaginationFooter,
  StatusTag,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
} from './styles';
import CustomTableHeader from '../../../components/CustomHeader';
import CustomPagination from '../../../components/CustomPagination';
import DetailModal from '../../../components/DetailModal'
import { Box, Divider, TableRow, Typography } from '@mui/material';

import {
  fetchFacilityShiftList,
  fetchShiftDetails,
  selectFacilityShiftLists,
  selectFacilityShiftsListsLoading,
  selectFacilityShiftsError,
  selectFacilityShiftsCount,
  IShift,
  IATTENDANCE_MANAGER
} from '../../../modules/shift';

import { selectFacilityId } from '../../../modules/facilityProfile';
import ShiftDetails from './components/ShiftDetails';
import WorkerAssigned from './components/WorkerAssigned';
import ShiftAction from './components/ShiftActions';

type Order = 'asc' | 'desc';

interface Column {
  id: keyof IShift | string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const COLUMN_MAPPING: { [key: string]: keyof IShift | string } = {
  'ID No': 'shift_id',
  'Date': 'start_date_time',
  'Start Time': 'start_date_time',
  'End Time': 'end_date_time',
  'Position': 'position_role',
  'No. of Workers': 'shift_assignee_count',
  'Appoint Member': 'assigned_workers',
  'Sleepover': 'is_sleepover',
  'Status': 'status',
};

const FACILITY_SHIFT_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
  id: id,
  label: label,
  numeric: false,
  disableSorting: !(id === 'title' || id === 'shift_id'),
}));


const FacilityShiftList: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setIsShow } = useBreadcrumb();
  useEffect(() => {
    setActivePage('manage_shifts');
    setIsShow(false);
  }, [setActivePage, setIsShow]);

  const dispatch = useDispatch<any>();
  const facilityShifts = useSelector(selectFacilityShiftLists);
  const facilityShiftsLoading = useSelector(selectFacilityShiftsListsLoading);
  const facilityShiftsError = useSelector(selectFacilityShiftsError);
  const facilityShiftsCount = useSelector(selectFacilityShiftsCount);
  const facilityId = useSelector(selectFacilityId);

  const [page, setPage] = useState(1);
  const rowsPerPage = 20;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IShift | string>('title');
  const [shiftDetails, setShiftDetails] = useState<IShift | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpenDrawer = () => setDrawerOpen(true);
  const handleCloseDrawer = () => setDrawerOpen(false);

  useEffect(() => {
    if (facilityId) {
      dispatch(fetchFacilityShiftList({ facilityId, page, pageSize: rowsPerPage, orderBy: orderBy as string, order }));
    }
  }, [dispatch, facilityId, page, order, orderBy, rowsPerPage]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property as keyof IShift | string;

    const columnDef = FACILITY_SHIFT_COLUMNS.find(col => col.id === property);
    if (columnDef?.disableSorting) {
      return;
    }

    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  const getStatus = (status: string) => {
    if (status === 'started' || status === 'partially_started') return 'Active';
    if (status === 'partially_assigned' || status === 'assigned') return 'Upcoming';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'completed') return 'Completed';
    if (status === 'partially_completed') return 'Partially Completed';
    if (status === 'scheduled') return 'Pending';
    if (status === 'rejected') return 'Rejected';
    if (status === 'partially_rejected') return 'Partially Rejected';
    return status;
  }

  const getAttendanceManagerName = (manager: IATTENDANCE_MANAGER) => {
    if (!manager) return '-';
    return `${manager.first_name} ${manager.last_name}`;
  }

  const onClickRow = (shift: IShift) => {
    setShiftDetails(shift);
    handleOpenDrawer();
  };

  const handleRefresh = async () => {
    if (facilityId) {
      dispatch(fetchFacilityShiftList({ facilityId, page, pageSize: rowsPerPage, orderBy: orderBy as string, order }));
      if (shiftDetails) {
        try {
          const result = await dispatch(fetchShiftDetails(shiftDetails.shift_id)).unwrap();
          // Manually casting result to IShift since IShift and IShiftDetails are similar enough for this view
          // and we want to refresh the view with the latest assignment status
          setShiftDetails(result as unknown as IShift);
        } catch (e) {
          console.error("Failed to refresh shift details", e);
        }
      }
    }
  };

  return (
    <PageWrapper>
      <Box sx={{ width: '100%', padding: 3 }}>
        <HeaderRow>
          <Typography variant="h5" style={{ fontWeight: 'bold', color: '#222' }}>
            Manage Shifts
          </Typography>
        </HeaderRow>
        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={FACILITY_SHIFT_COLUMNS}
              order={order}
              orderBy={orderBy as string}
              onRequestSort={handleRequestSort}
            />
            <StyledTableBody>
              {facilityShiftsLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={FACILITY_SHIFT_COLUMNS.length} align="center">
                    Loading...
                  </StyledTableCell>
                </TableRow>
              ) : facilityShiftsError || !facilityShifts || !facilityShifts.length ? (
                <TableRow>
                  <StyledTableCell colSpan={FACILITY_SHIFT_COLUMNS.length} align="center">
                    No data available
                  </StyledTableCell>
                </TableRow>
              ) : (
                facilityShifts.map((shift) => (
                  <TableRow
                    key={shift.shift_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => onClickRow(shift)}
                  >
                    <StyledTableCell align="left">{shift.shift_id}</StyledTableCell>
                    <StyledTableCell align="left">{dayjs(shift.start_date_time).format('DD/MM/YYYY')}</StyledTableCell>
                    <StyledTableCell align="left">{dayjs(shift.start_date_time).format('hh:mm A')}</StyledTableCell>
                    <StyledTableCell align="left">{dayjs(shift.end_date_time).format('hh:mm A')}</StyledTableCell>
                    <StyledTableCell align="left">{shift.position_role}</StyledTableCell>
                    <StyledTableCell align="left">{shift.shift_assignee_count} / {shift.workers_required}</StyledTableCell>
                    <StyledTableCell align="left">{getAttendanceManagerName(shift.attendance_manager)}</StyledTableCell>
                    <StyledTableCell align="left">{shift.is_sleepover ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell align="left">
                      <StatusTag status={getStatus(shift.status)}>
                        {getStatus(shift.status)}
                      </StatusTag>
                    </StyledTableCell>
                  </TableRow>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>
        <PaginationFooter>
          <Box>
            Showing {facilityShifts?.length || 0} of {facilityShiftsCount} results
          </Box>
          <CustomPagination
            currentPage={page}
            totalPages={Math.ceil(facilityShiftsCount / rowsPerPage)}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </PaginationFooter>
      </Box>
      {shiftDetails &&
        <DetailModal
          open={drawerOpen}
          onClose={handleCloseDrawer}
          shiftId={shiftDetails.shift_id}
          shiftStatus={getStatus(shiftDetails.status)}
        >
          <ShiftDetails
            date={dayjs(shiftDetails.start_date_time).format('DD/MM/YYYY')}
            position={shiftDetails.position_role}
            startTime={dayjs(shiftDetails.start_date_time).format('HH:mm')}
            endTime={dayjs(shiftDetails.end_date_time).format('HH:mm')}
            numberOfWorkers={`${shiftDetails.shift_assignment ? shiftDetails.shift_assignment.filter((a: any) => a.status !== 'worker_cancelled').length : 0}/${shiftDetails.workers_required}`}
            experience={shiftDetails.experience_required || '-'}
            appointedMember={getAttendanceManagerName(shiftDetails.attendance_manager)}
            shift_id={shiftDetails.shift_id}
            status={shiftDetails.status}
            shiftEndDateTime={shiftDetails.end_date_time}
            isSleepover={shiftDetails.is_sleepover}
            label={shiftDetails.label}
          />
          <Divider variant="middle" />
          {shiftDetails.shift_assignment && shiftDetails.shift_assignment.length > 0 && shiftDetails.shift_assignment.map((assignment, index) => (
            <WorkerAssigned
              key={index}
              name={assignment.worker.user.first_name + ' ' + assignment.worker.user.last_name}
              id={assignment.worker.worker_id}
              status={assignment.status}
              avatarUrl={assignment.worker.user.profile_picture || ''}
              clockIn={assignment.clock_in_time}
              clockOut={assignment.clock_out_time}
              shiftStart={shiftDetails.start_date_time}
              shiftEnd={shiftDetails.end_date_time}
              isLate={assignment.late_arrival}
              assignmentId={assignment.assignment_id}
              workerRemarks={assignment.worker_remarks}
              shiftId={shiftDetails.shift_id}
              workerSuggestedTime={assignment.worker_suggested_time}
              facilityRemarks={assignment.facility_remarks || ''}
              onRefresh={handleRefresh}
            />
          ))}
          <ShiftAction
            status={shiftDetails.status}
            shiftId={shiftDetails.shift_id}
          />
        </DetailModal>
      }
    </PageWrapper>
  );
};

export default FacilityShiftList;